import React, { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import {
	CustomButton,
	CustomChips,
	CustomTextField,
	CustomTooltip,
	IconComponent,
	icon,
} from "../../../../../../common/components";
import styles from "./AICreationContent.module.css";
import { translate } from "../../../../../../common/providers";
import { useApi, useToggle } from "../../../../../../common/hooks";
import { NotesService } from "../../../../../../api";
import { isNonEmptyArray } from "../../../../../../common/utils";
import { HARDCODED_PROMPTS } from "../../../../constants/constants";

const AICreationContent = ({ onSubmit }) => {
	const [suggestions, setSuggestions] = useState([]);
	const [additionPrompt, setAdditionPrompt] = useState("");
	const isGenerating = useSelector(({ note }) => note.isGenerating);
	const hasAvailableAIQuota = useSelector(({ context }) => context?.hasAvailableAIQuota);
	const projectId = useSelector(({ context }) => context?.project?.id);
	const [openSuggestions, toggleSuggestions] = useToggle(true);
	const { call: getNoteSuggestions } = useApi(NotesService.getNoteSuggestions);
	useEffect(() => {
		getNoteSuggestions({ projectId })
			.then((data) =>
				setSuggestions(
					[
						...new Set([
							...data,
							...HARDCODED_PROMPTS.map((prompt) => ({
								title: translate(prompt.title),
								prompt: translate(prompt.prompt),
							})),
						]),
					].slice(0, 6)
				)
			)
			.catch(console.error);
	}, [getNoteSuggestions, projectId]);
	const handleChangeAdditionPrompt = (event) => {
		setAdditionPrompt(event.target.value);
		if (openSuggestions) {
			toggleSuggestions();
		}
	};
	const handleSubmit = () => {
		onSubmit({
			additionPrompt,
			documents: {},
			integrationPolicy: "REPLACE",
			selectDocumentAutomatically: true,
		});
	};
	return (
		<div className={styles.content}>
			<div>
				<span className={styles.iconWrapper}>
					<IconComponent color="var(--component-text-blue)" icon={icon.faWandMagicSparkles} size="5xl" />
				</span>
			</div>
			<div className={styles.title}>{translate("notes.new-note.ai-creation-dialog.title")}</div>
			<div className={styles.subTitle}>{translate("notes.new-note.ai-creation-dialog.sub-title")}</div>
			<CustomTextField
				fullWidth
				multiline
				className={styles.textfield}
				disabled={isGenerating}
				placeholder={translate("notes.new-note.ai-creation-dialog.placeholder")}
				rows={8}
				value={additionPrompt}
				onChange={handleChangeAdditionPrompt}
			/>
			<CustomTooltip
				title={(!hasAvailableAIQuota && translate("common:company.quotas.consumption-reached.tooltip")) || ""}
			>
				<span>
					<CustomButton
						disabled={isGenerating || !hasAvailableAIQuota}
						variant="contained"
						onClick={handleSubmit}
					>
						{translate("notes.new-note.ai-creation-dialog.button")}
					</CustomButton>
				</span>
			</CustomTooltip>
			{!isGenerating && isNonEmptyArray(suggestions) && (
				<div className={styles.suggestionsContainer}>
					<CustomButton className={styles.showButton} variant="text" onClick={() => toggleSuggestions()}>
						{translate(
							`notes.new-note.ai-creation-dialog.suggestions.${(openSuggestions && "hide") || "show"}`
						)}
						<span className={styles.suggestionIcon}>
							<IconComponent
								color="var(--blue-500)"
								icon={icon[(openSuggestions && "faChevronUp") || "faChevronDown"]}
							/>
						</span>
					</CustomButton>
					{!isGenerating && (
						<div className={styles.suggestionsChipContainer}>
							{openSuggestions &&
								suggestions.map((suggestion) => (
									<CustomChips
										key={suggestion.title}
										className={styles.suggestionChip}
										isActive={suggestion.prompt === additionPrompt}
										label={suggestion.title}
										onClick={() => setAdditionPrompt(suggestion.prompt)}
									/>
								))}
						</div>
					)}
				</div>
			)}
		</div>
	);
};

export default AICreationContent;
